import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useUserStore } from '@stores/user'

// 创建 axios 实例
const request: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_APP_API_BASE_URL || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 请求队列（用于取消重复请求）
const pendingMap = new Map<string, AbortController>()

// 生成请求key
const getRequestKey = (config: AxiosRequestConfig): string => {
  return `${config.method}_${config.url}_${JSON.stringify(config.params)}_${JSON.stringify(config.data)}`
}

// 添加请求到队列
const addPending = (config: AxiosRequestConfig): void => {
  const key = getRequestKey(config)
  const controller = new AbortController()
  config.signal = controller.signal
  pendingMap.set(key, controller)
}

// 移除请求从队列
const removePending = (config: AxiosRequestConfig): void => {
  const key = getRequestKey(config)
  if (pendingMap.has(key)) {
    const controller = pendingMap.get(key)
    controller?.abort()
    pendingMap.delete(key)
  }
}

// 刷新token的请求路径
const REFRESH_TOKEN_URL = '/learner/auth/refresh'

// 扩展 AxiosRequestConfig 支持 silent 标记
declare module 'axios' {
  interface AxiosRequestConfig {
    /** 静默模式，不弹出错误消息提示 */
    silent?: boolean
  }
}

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    // 移除重复请求
    removePending(config)
    addPending(config)

    // 添加token（刷新token的请求除外）
    const userStore = useUserStore()
    if (userStore.token && config.url !== REFRESH_TOKEN_URL) {
      config.headers.Authorization = `Bearer ${userStore.token}`
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
request.interceptors.response.use(
  (response: AxiosResponse) => {
    // 移除已完成的请求
    removePending(response.config)

    // 文件下载直接返回 response
    const contentType = response.headers['content-type']
    if (contentType && (
      contentType.includes('application/octet-stream') ||
      contentType.includes('application/pdf') ||
      contentType.includes('image/') ||
      contentType.includes('video/')
    )) {
      return response
    }

    const { code, message } = response.data

    // 业务成功 (后端 R<T> 包装，code 为 200 表示成功)
    if (code === 200) {
      return response.data
    }

    // 业务错误
    if (!response.config.silent) {
      ElMessage.error(message || '请求失败')
    }
    return Promise.reject(new Error(message || '请求失败'))
  },
  async (error) => {
    // 移除失败的请求
    if (error.config) {
      removePending(error.config)
    }

    // 处理取消请求
    if (axios.isCancel(error)) {
      return Promise.reject(error)
    }

    const { response } = error

    if (response) {
      const { status, data } = response
      const silent = error.config?.silent

      switch (status) {
        case 401:
          if (error.config?.url === REFRESH_TOKEN_URL) {
            return Promise.reject(error)
          }
          return handleTokenExpired(error.config)
        case 403:
          if (!silent) ElMessage.error('没有权限执行此操作')
          break
        case 404:
          if (!silent) ElMessage.error('请求的资源不存在')
          break
        case 500:
          if (!silent) ElMessage.error(data?.message || '服务器内部错误')
          break
        default:
          if (!silent) ElMessage.error(data?.message || `请求失败: ${status}`)
      }
    } else {
      if (!error.config?.silent) {
        ElMessage.error('网络错误，请检查网络连接')
      }
    }

    return Promise.reject(error)
  }
)

// 处理 Token 过期
let isRefreshing = false

interface RefreshSubscriber {
  resolve: (value: unknown) => void
  reject: (error: Error) => void
  config: AxiosRequestConfig
}
let refreshSubscribers: RefreshSubscriber[] = []

const addRefreshSubscriber = (subscriber: RefreshSubscriber) => {
  refreshSubscribers.push(subscriber)
}

const onTokenRefreshed = (newToken: string) => {
  refreshSubscribers.forEach(sub => {
    if (sub.config.headers) {
      sub.config.headers.Authorization = `Bearer ${newToken}`
    }
    sub.resolve(request(sub.config))
  })
  refreshSubscribers = []
}

const onTokenRefreshFailed = (error: Error) => {
  refreshSubscribers.forEach(sub => sub.reject(error))
  refreshSubscribers = []
}

const handleTokenExpired = async (originalConfig: AxiosRequestConfig): Promise<unknown> => {
  const userStore = useUserStore()

  if (!userStore.refreshToken) {
    // 没有刷新令牌，直接跳转登录
    ElMessageBox.confirm('登录已过期，请重新登录', '提示', {
      confirmButtonText: '去登录',
      cancelButtonText: '取消',
      type: 'warning',
    }).then(() => {
      userStore.logout()
      window.location.href = '/auth'
    })
    return Promise.reject(new Error('登录已过期'))
  }

  // 如果正在刷新，将请求加入队列等待
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      addRefreshSubscriber({ resolve, reject, config: originalConfig })
    })
  }

  isRefreshing = true

  try {
    const success = await userStore.refreshAccessToken()
    if (success) {
      const newToken = userStore.token
      // 通知所有等待的请求
      onTokenRefreshed(newToken)
      // 重发原始请求
      if (originalConfig.headers) {
        originalConfig.headers.Authorization = `Bearer ${newToken}`
      }
      return request(originalConfig)
    } else {
      throw new Error('刷新失败')
    }
  } catch (error) {
    onTokenRefreshFailed(error as Error)
    ElMessageBox.confirm('登录已过期，请重新登录', '提示', {
      confirmButtonText: '去登录',
      cancelButtonText: '取消',
      type: 'warning',
    }).then(() => {
      userStore.logout()
      window.location.href = '/auth'
    })
    return Promise.reject(error)
  } finally {
    isRefreshing = false
  }
}

// 封装请求方法
export const http = {
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return request.get(url, config)
  },
  post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return request.post(url, data, config)
  },
  put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return request.put(url, data, config)
  },
  delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return request.delete(url, config)
  },
  patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return request.patch(url, data, config)
  },
}

export default request
